import { useEffect } from 'react';
import { useLocation } from 'react-router-dom';

declare global {
  interface Window {
    gtag: (command: string, targetId: string | Date, config?: any) => void;
    dataLayer: any[];
  }
}

interface GoogleAnalyticsProps {
  trackingId: string;
}

const GoogleAnalytics: React.FC<GoogleAnalyticsProps> = ({ trackingId }) => {
  const location = useLocation();

  useEffect(() => {
    // Only initialize once and prevent duplicate scripts
    if (document.querySelector(`script[src*="gtag/js?id=${trackingId}"]`)) {
      return;
    }

    // Initialize dataLayer
    window.dataLayer = window.dataLayer || [];
    window.gtag = function() {
      window.dataLayer.push(arguments);
    };

    // Load Google Analytics script
    const script = document.createElement('script');
    script.async = true;
    script.src = `https://www.googletagmanager.com/gtag/js?id=${trackingId}`;
    script.onload = () => {
      // Initialize GA4 after script loads
      window.gtag('js', new Date());
      window.gtag('config', trackingId, {
        send_page_view: false, // We'll manually send page views
        debug_mode: import.meta.env.DEV
      });
      
      // Send initial page view
      window.gtag('event', 'page_view', {
        page_title: document.title,
        page_location: window.location.href,
        page_path: location.pathname
      });

      console.log('Google Analytics initialized with ID:', trackingId);
    };
    
    script.onerror = () => {
      console.error('Failed to load Google Analytics script');
    };

    document.head.appendChild(script);
  }, [trackingId]);

  useEffect(() => {
    // Track page views on route changes (skip initial load)
    if (typeof window.gtag === 'function') {
      window.gtag('event', 'page_view', {
        page_title: document.title,
        page_location: window.location.href,
        page_path: location.pathname
      });
      
      console.log('Page view tracked:', location.pathname);
    }
  }, [location.pathname]); // Only track when pathname changes

  return null;
};

// Enhanced analytics tracking functions with better error handling
export const trackEvent = (eventName: string, parameters?: any) => {
  try {
    if (typeof window !== 'undefined' && typeof window.gtag === 'function') {
      window.gtag('event', eventName, {
        ...parameters,
        timestamp: new Date().toISOString()
      });
      console.log('Event tracked:', eventName, parameters);
    } else {
      console.warn('Google Analytics not initialized yet');
    }
  } catch (error) {
    console.error('Error tracking event:', error);
  }
};

export const trackCourseClick = (courseName: string, courseUrl: string) => {
  trackEvent('course_click', {
    course_name: courseName,
    course_url: courseUrl,
    event_category: 'engagement',
    event_label: courseName,
    custom_parameter_1: 'udemy_redirect'
  });
};

export const trackBundleClick = (bundleName: string, bundleId: string) => {
  trackEvent('bundle_view', {
    bundle_name: bundleName,
    bundle_id: bundleId,
    event_category: 'engagement',
    event_label: bundleName
  });
};

export const trackEnrollmentClick = (courses: any[]) => {
  trackEvent('enrollment_click', {
    event_category: 'conversion',
    event_label: 'bundle_enrollment',
    course_count: courses.length,
    courses: courses.map(c => c.title).join(', '),
    value: courses.length,
    currency: 'USD'
  });
};

export const trackTestimonialView = (testimonialName: string) => {
  trackEvent('testimonial_view', {
    testimonial_name: testimonialName,
    event_category: 'engagement',
    event_label: testimonialName
  });
};

// New tracking functions for better insights
export const trackSearchQuery = (query: string, results: number) => {
  trackEvent('search', {
    search_term: query,
    search_results: results,
    event_category: 'engagement'
  });
};

export const trackDownload = (fileName: string, fileType: string) => {
  trackEvent('file_download', {
    file_name: fileName,
    file_type: fileType,
    event_category: 'engagement'
  });
};

export const trackVideoPlay = (videoTitle: string, videoDuration?: number) => {
  trackEvent('video_play', {
    video_title: videoTitle,
    video_duration: videoDuration,
    event_category: 'engagement'
  });
};

export const trackFormSubmit = (formName: string, formType: string) => {
  trackEvent('form_submit', {
    form_name: formName,
    form_type: formType,
    event_category: 'conversion'
  });
};

export default GoogleAnalytics;